import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { apiConfig } from "../config/api";
import {
  Check,
  Circle,
  X,
  Eye,
  EyeOff,
  UserRound,
  Mail,
  Lock,
  ImagePlus,
  Loader2,
} from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";

function DoctorProfile() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    image: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [preview, setPreview] = useState("/img/doctors/avatar.png");
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getImagePreviewUrl = (imageValue) => {
    if (!imageValue) return "/img/doctors/avatar.png";

    const imageUrl = apiConfig.getDoctorImage(imageValue);

    return `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}v=${Date.now()}`;
  };

  useEffect(() => {
    if (user && user.role !== "doctor") {
      navigate("/");
      return;
    }

    const fetchProfile = async () => {
      try {
        setPageLoading(true);

        const token = localStorage.getItem("token");

        // Use the correct endpoint from apiConfig
        const { data } = await axios.get(apiConfig.getMyProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // The backend returns a combined object. 
        // Preference: data.doctor > data.user > data
        const doctorData = data?.doctor || data;
        const userData = data?.user || data;

        setForm((prev) => ({
          ...prev,
          name: doctorData?.name || userData?.name || "",
          email: userData?.email || data?.email || "",
        }));

        const currentImage = doctorData?.image || userData?.image || data?.image;
        
        if (currentImage) {
          setPreview(getImagePreviewUrl(currentImage));
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Failed to load doctor profile");
      } finally {
        setPageLoading(false);
      }
    };

    if (user) {
      fetchProfile();
    }
  }, [user, navigate]);

  const passwordRules = useMemo(() => {
    const p = form.password;

    return {
      length: p.length >= 8,
      uppercase: /[A-Z]/.test(p),
      lowercase: /[a-z]/.test(p),
      number: /\d/.test(p),
    };
  }, [form.password]);

  const strengthCount = Object.values(passwordRules).filter(Boolean).length;

  const getStrengthLabel = () => {
    if (form.password === "") return "";
    if (strengthCount <= 1) return "Weak";
    if (strengthCount === 2) return "Fair";
    if (strengthCount === 3) return "Good";
    return "Strong";
  };

  const getStrengthColor = () => {
    if (strengthCount === 1) return "bg-red-500";
    if (strengthCount === 2) return "bg-yellow-500";
    if (strengthCount === 3) return "bg-[#008e9b]";
    if (strengthCount === 4) return "bg-green-500";
    return "bg-gray-200 dark:bg-[#1f3a40]";
  };

  const passwordsMatch =
    form.confirmPassword !== "" && form.password === form.confirmPassword;

  const isPasswordValid = !form.password || strengthCount === 4;
  const isConfirmValid = !form.password || passwordsMatch;

  const isFormValid =
    form.name.trim() !== "" &&
    form.email.trim() !== "" &&
    isPasswordValid &&
    isConfirmValid;

  const labelClass =
    "mb-1.5 block text-sm font-bold text-gray-700 dark:text-slate-300";

  const iconClass =
    "absolute left-4 top-1/2 -translate-y-1/2 text-[#008e9b] dark:text-[#46daea]";

  const inputClass =
    "w-full rounded-2xl border border-gray-200 bg-gray-50 py-4 pl-12 pr-4 text-sm font-semibold text-gray-800 outline-none transition-all focus:border-transparent focus:bg-white focus:ring-2 focus:ring-[#008e9b] dark:border-[#1f3a40] dark:bg-[#071416] dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-[#071416] dark:focus:ring-[#46daea]";

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      const file = files[0];

      setForm((prev) => ({ ...prev, image: file }));
      setPreview(URL.createObjectURL(file));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      if (form.password && !passwordsMatch) {
        toast.error("Password and confirm password must match");
      } else if (form.password && !isPasswordValid) {
        toast.error(
          "Please make sure your new password meets the requirements.",
        );
      } else {
        toast.error("Please fill all required fields correctly.");
      }

      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token");
      const payload = new FormData();

      if (form.name) payload.append("name", form.name);
      if (form.email) payload.append("email", form.email);
      if (form.password) payload.append("password", form.password);
      
      // ONLY send image if a new file was selected
      if (form.image instanceof File) {
        payload.append("image", form.image);
      }

      const { data } = await axios.put(apiConfig.updateDoctorProfile, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // The backend returns { message, user, doctor }
      const updatedUser = data?.user || null;
      const updatedDoctor = data?.doctor || null;

      // Update local storage for immediate header/UI refresh elsewhere
      if (updatedUser) {
        localStorage.setItem("userData", JSON.stringify(updatedUser));
      }

      // Update preview with the new URL returned from the server (ImageKit URL)
      const newImageUrl = updatedDoctor?.image || updatedUser?.image || data?.image;

      if (newImageUrl) {
        setPreview(getImagePreviewUrl(newImageUrl));
      }

      toast.success(data.message || "Profile updated successfully");

      // Reset sensitive fields but keep basic info
      setForm((prev) => ({
        ...prev,
        password: "",
        confirmPassword: "",
        image: null,
      }));

      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (error) {
      console.error("Update error:", error);
      const message =
        error?.response?.data?.message || "Failed to update profile";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const RuleItem = ({ valid, text }) => (
    <div
      className={`flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-black transition-all duration-300 ${
        valid
          ? "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-300"
          : "bg-gray-50 text-gray-500 dark:bg-[#071416] dark:text-slate-400"
      }`}
    >
      {valid ? (
        <Check size={14} className="stroke-[3px]" />
      ) : (
        <Circle
          size={14}
          className="fill-gray-300 stroke-none dark:fill-slate-600"
        />
      )}

      <span>{text}</span>
    </div>
  );

  if (!user || user.role !== "doctor") return null;

  if (pageLoading) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#f4fbfc] via-white to-[#eefcff] transition-colors dark:from-[#071416] dark:via-[#0b1d20] dark:to-[#102b30]">
        <LoadingSpinner text="Loading your doctor profile..." fullScreen />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#f4fbfc] via-white to-[#eefcff] px-4 py-8 transition-colors dark:from-[#071416] dark:via-[#0b1d20] dark:to-[#102b30] sm:px-6">
      <div className="mx-auto mb-8 max-w-2xl text-center">
        <div className="mb-3 inline-flex rounded-full bg-[#e8fbfd] px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#008e9b] dark:bg-[#46daea]/15 dark:text-[#46daea]">
          Doctor Area
        </div>

        <h2 className="text-3xl font-black text-gray-900 dark:text-white sm:text-4xl">
          Doctor Profile
        </h2>

        <p className="mx-auto mt-2 max-w-xl text-sm font-medium text-gray-500 dark:text-slate-400">
          Update your profile information, profile image, and account security.
        </p>
      </div>

      <div className="mx-auto max-w-2xl overflow-hidden rounded-[2rem] border border-white bg-white/95 p-6 shadow-[0_30px_90px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-[#1f3a40] dark:bg-[#0f2428]/95 dark:shadow-[0_30px_90px_rgba(0,0,0,0.4)] sm:p-8">
        <form
          className="space-y-5"
          onSubmit={handleSubmit}
          encType="multipart/form-data"
        >
          <div className="rounded-[1.5rem] border border-gray-100 bg-gray-50 p-5 dark:border-[#1f3a40] dark:bg-[#071416]/70">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
              <div className="h-28 w-28 overflow-hidden rounded-full border-4 border-white bg-white shadow-lg dark:border-[#46daea]/20 dark:bg-[#071416]">
                <img
                  src={preview || "/img/doctors/avatar.png"}
                  alt="Doctor profile"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = "/img/doctors/avatar.png";
                  }}
                />
              </div>

              <div className="w-full flex-1 text-center sm:text-left">
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-[#008e9b] shadow-sm dark:bg-[#0f2428] dark:text-[#46daea]">
                  <ImagePlus size={15} />
                  Profile Image
                </div>

                <p className="mb-3 text-sm font-medium text-gray-500 dark:text-slate-400">
                  Your current profile image appears here. Choose a new image
                  only if you want to replace it.
                </p>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleChange}
                  className="w-full cursor-pointer text-xs text-gray-500 file:mr-4 file:rounded-full file:border-0 file:bg-cyan-50 file:px-4 file:py-2 file:text-xs file:font-bold file:text-cyan-700 hover:file:bg-cyan-100 dark:text-slate-400 dark:file:bg-[#46daea]/15 dark:file:text-[#46daea] dark:hover:file:bg-[#46daea]/25"
                />
              </div>
            </div>
          </div>

          <div>
            <label className={labelClass}>Name</label>

            <div className="relative">
              <UserRound size={18} className={iconClass} />

              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                type="text"
                className={inputClass}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Email</label>

            <div className="relative">
              <Mail size={18} className={iconClass} />

              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                type="email"
                className={inputClass}
                required
              />
            </div>
          </div>

          <div className="mt-7 border-t border-gray-100 pt-7 dark:border-[#1f3a40]">
            <div className="mb-5">
              <h3 className="text-xl font-black text-gray-900 dark:text-white">
                Account Security
              </h3>

              <p className="mt-1 text-sm font-medium text-gray-500 dark:text-slate-400">
                Leave the password fields empty if you do not want to change
                your password.
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label className={labelClass}>New Password</label>

                <div className="relative">
                  <Lock size={18} className={iconClass} />

                  <input
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    type={showPassword ? "text" : "password"}
                    placeholder="Leave empty to keep current"
                    className={`${inputClass} pr-12`}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 !border-none !bg-transparent !p-0 !text-gray-400 !shadow-none transition-colors hover:!bg-transparent hover:!text-[#008e9b] dark:hover:!text-[#46daea]"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                  </button>
                </div>

                {form.password && (
                  <div className="mt-4 rounded-3xl border border-gray-100 bg-white p-4 shadow-sm dark:border-[#1f3a40] dark:bg-[#071416]">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 dark:text-slate-500">
                        Security Check
                      </span>

                      <span
                        className={`text-xs font-black uppercase ${
                          strengthCount <= 1
                            ? "text-red-500 dark:text-red-300"
                            : strengthCount === 2
                              ? "text-yellow-600 dark:text-yellow-300"
                              : strengthCount === 3
                                ? "text-[#008e9b] dark:text-[#46daea]"
                                : "text-green-600 dark:text-green-300"
                        }`}
                      >
                        Strength: {getStrengthLabel()}
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-1.5">
                      {[1, 2, 3, 4].map((index) => (
                        <div
                          key={index}
                          className={`h-2 rounded-full transition-all duration-500 ${
                            index <= strengthCount
                              ? getStrengthColor()
                              : "bg-gray-200 dark:bg-[#1f3a40]"
                          }`}
                        />
                      ))}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <RuleItem
                        valid={passwordRules.length}
                        text="8+ Characters"
                      />
                      <RuleItem
                        valid={passwordRules.uppercase}
                        text="Uppercase"
                      />
                      <RuleItem
                        valid={passwordRules.lowercase}
                        text="Lowercase"
                      />
                      <RuleItem valid={passwordRules.number} text="Number" />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>Confirm New Password</label>

                <div className="relative">
                  <Lock size={18} className={iconClass} />

                  <input
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter new password"
                    className={`w-full rounded-2xl border bg-gray-50 py-4 pl-12 pr-12 text-sm font-semibold text-gray-800 outline-none transition-all focus:border-transparent focus:bg-white focus:ring-2 dark:bg-[#071416] dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-[#071416] ${
                      form.confirmPassword === ""
                        ? "border-gray-200 focus:ring-[#008e9b] dark:border-[#1f3a40] dark:focus:ring-[#46daea]"
                        : passwordsMatch
                          ? "border-green-200 focus:ring-green-500 dark:border-green-500/40"
                          : "border-red-200 focus:ring-red-500 dark:border-red-500/40"
                    }`}
                    required={!!form.password}
                  />

                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 !border-none !bg-transparent !p-0 !text-gray-400 !shadow-none transition-colors hover:!bg-transparent hover:!text-[#008e9b] dark:hover:!text-[#46daea]"
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={19} />
                    ) : (
                      <Eye size={19} />
                    )}
                  </button>
                </div>

                {form.confirmPassword !== "" && form.password !== "" && (
                  <div className="ml-1 mt-2 flex items-center gap-1.5">
                    {passwordsMatch ? (
                      <div className="flex items-center gap-1 text-xs font-black uppercase tracking-tight text-green-600 dark:text-green-300">
                        <Check size={14} strokeWidth={3} />
                        Passwords match
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs font-black uppercase tracking-tight text-red-500 dark:text-red-300">
                        <X size={14} strokeWidth={3} />
                        Passwords do not match
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || !isFormValid}
            className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-black text-white shadow-lg transition-all duration-300 ${
              isFormValid && !saving
                ? "!bg-[#008e9b] hover:-translate-y-0.5 hover:!bg-[#007a85] hover:shadow-xl dark:!bg-[#46daea] dark:text-[#071416] dark:hover:!bg-[#7ee9f2]"
                : "cursor-not-allowed !bg-gray-400 opacity-70"
            }`}
          >
            {saving ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Saving changes...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </form>
      </div>
    </main>
  );
}

export default DoctorProfile;
